/**
 * Profile Switcher Modal
 * 
 * Modal component for switching between profiles without logging out.
 * Matches the Flutter TV implementation pattern.
 * 
 * Legacy CSS: Uses table layout and inline-block for old Chromium compatibility.
 */
import React, { useEffect, useCallback } from "react";
import {
    useFocusable,
    FocusContext,
    setFocus,
} from "@noriginmedia/norigin-spatial-navigation";
import toast from "react-hot-toast";
import { trackGoogleAnalyticsEvent } from "../../services/googleAnalytics";
import { getAuthErrorMessage, switchProfile } from "../../services/authService";
import { getAccessToken } from "../../services/authStorage";
import { resolveAuthMediaUrl } from "../../utils/api.utils";
import {
    PROFILE_COLORS,
    PROFILE_FONT_SIZES,
    PROFILE_SPACING,
    PROFILE_RADIUS,
    PROFILE_SHADOWS,
    FONT_FAMILY,
} from "./styles/profileStyles";

// ============================================================================
// JWT DECODE HELPER (without verification)
// ============================================================================

/**
 * Decode JWT payload without verification.
 * Used to extract profile ID from access token.
 * Matches Flutter TokenStorage.getProfileIdFromToken() behavior.
 */
function decodeJwtPayload(token) {
    try {
        if (!token || typeof token !== 'string') {
            return null;
        }
        var parts = token.split('.');
        if (parts.length !== 3) {
            return null;
        }
        // Decode base64url payload (second part)
        var payload = parts[1];
        // Replace base64url characters with base64
        payload = payload.replace(/-/g, '+').replace(/_/g, '/');
        // Add padding if needed
        var pad = payload.length % 4;
        if (pad) {
            payload += '='.repeat(4 - pad);
        }
        var decoded = atob(payload);
        return JSON.parse(decoded);
    } catch (error) {
        console.warn('[ProfileSwitch] JWT decode error:', error);
        return null;
    }
}

/**
 * Get profile ID from stored access token.
 */
function getProfileIdFromToken() {
    var token = getAccessToken();
    if (!token) return null;

    var payload = decodeJwtPayload(token);
    if (!payload) return null;

    // Profile ID is typically stored as 'profileId' or 'profile_id' in JWT
    return payload.profileId || payload.profile_id || payload.pid || null;
}

// ============================================================================
// PWA RESTART HELPER
// ============================================================================

/**
 * Perform a complete PWA restart to re-fetch all private data.
 * Matches the Android TV AppRestartHelper.softRestartForTvProfileSwitch() behavior.
 * 
 * Steps:
 * 1. Clear all caches (CacheStorage API)
 * 2. Unregister service workers
 * 3. Clear user-specific cached data from localStorage
 * 4. Navigate to root with cache-bust parameter
 */
async function performPwaRestart() {
    try {
        var pwaEnabled = !(typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development');

        // 1. Clear all caches
        if (pwaEnabled && 'caches' in window) {
            var cacheNames = await caches.keys();
            await Promise.all(
                cacheNames.map(function (cacheName) {
                    return caches.delete(cacheName);
                })
            );
            console.log('[ProfileSwitch] Cleared all caches');
        }

        // 2. Unregister all service workers
        if (pwaEnabled && 'serviceWorker' in navigator) {
            var registrations = await navigator.serviceWorker.getRegistrations();
            await Promise.all(
                registrations.map(function (registration) {
                    return registration.unregister();
                })
            );
            console.log('[ProfileSwitch] Unregistered service workers');
        }

        // 3. Clear user-specific cached data from localStorage
        // Keep only auth tokens, remove cached private data
        var keysToRemove = [];
        for (var i = 0; i < localStorage.length; i++) {
            var key = localStorage.key(i);
            // Remove cached data but keep tokens
            if (key && (
                key.includes('cache') ||
                key.includes('Cache') ||
                key.includes('privateData') ||
                key.includes('userData') ||
                key.includes('profile_') ||
                key.includes('showcase')
            )) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach(function (key) {
            localStorage.removeItem(key);
        });
        console.log('[ProfileSwitch] Cleared cached local data');

    } catch (error) {
        console.warn('[ProfileSwitch] Error clearing caches:', error);
    }

    // 4. Navigate to root with cache-bust parameter for complete restart
    var bustParam = 'profile_switch=' + Date.now();
    window.location.href = '/?' + bustParam;
}

// ============================================================================
// STYLED COMPONENTS (Legacy TV compatible)
// ============================================================================

function modalElement(tag, className) {
    var ModalElement = React.forwardRef(function ModalElement(props, ref) {
        var mergedClassName = className + (props.className ? " " + props.className : "");
        return React.createElement(tag, Object.assign({}, props, { className: mergedClassName, ref: ref }));
    });

    ModalElement.displayName = "ModalElement";
    return ModalElement;
}

var Overlay = React.forwardRef(function Overlay(props, ref) {
    var overlayProps = Object.assign({}, props);
    delete overlayProps.$open;
    overlayProps.className =
        "legacy-profile-switcher-overlay" +
        (props.$open ? " is-open" : "") +
        (props.className ? " " + props.className : "");
    overlayProps.ref = ref;
    return <div {...overlayProps} />;
});
Overlay.displayName = "Overlay";
var OverlayCell = modalElement("div", "legacy-profile-switcher-cell");
var ModalCard = modalElement("div", "legacy-profile-switcher-card");
var ModalTitle = modalElement("div", "legacy-profile-switcher-title");
var ProfilesContainer = modalElement("div", "legacy-profile-switcher-profiles");
var ProfileCardWrapper = modalElement("div", "legacy-profile-switcher-profile-wrap");
var ProfileCardStyled = React.forwardRef(function ProfileCardStyled(props, ref) {
    var cardProps = Object.assign({}, props);
    delete cardProps.$focused;
    delete cardProps.$isSelected;
    cardProps.className =
        "legacy-profile-switcher-profile-card" +
        (props.$focused ? " is-focused" : "") +
        (props.$isSelected ? " is-selected" : "") +
        (props.className ? " " + props.className : "");
    cardProps.ref = ref;
    return <div {...cardProps} />;
});
ProfileCardStyled.displayName = "ProfileCardStyled";
var ProfileAvatar = function ProfileAvatar(props) {
    var style = Object.assign(
        {
            background: props.$hasImage
                ? "url(" + props.$imageUrl + ")"
                : "linear-gradient(135deg, #555 0%, #333 100%)",
            backgroundSize: "cover",
            backgroundPosition: "center",
        },
        props.style || {}
    );
    return (
        <div className={"legacy-profile-switcher-avatar" + (props.$isSelected ? " is-selected" : "")} style={style}>
            {props.children}
        </div>
    );
};
var AvatarInner = modalElement("div", "legacy-profile-switcher-avatar-inner");
var ProfileName = modalElement("div", "legacy-profile-switcher-name");
var ActiveBadge = modalElement("div", "legacy-profile-switcher-active-badge");
var AgeLabel = modalElement("div", "legacy-profile-switcher-age");
var CancelButton = React.forwardRef(function CancelButton(props, ref) {
    var buttonProps = Object.assign({}, props);
    delete buttonProps.$focused;
    buttonProps.className =
        "legacy-profile-switcher-cancel" +
        (props.$focused ? " is-focused" : "") +
        (props.className ? " " + props.className : "");
    buttonProps.ref = ref;
    return <button {...buttonProps} />;
});
CancelButton.displayName = "CancelButton";
var LoadingSpinner = modalElement("div", "legacy-profile-switcher-spinner");

// ============================================================================
// AGE RANGE HELPER
// ============================================================================

function getAgeRangeLabel(age) {
    var ageNumber = Number(age);
    if (ageNumber === 120) {
        return "بزرگسال";
    } else if (ageNumber > 0 && ageNumber < 120) {
        return "تا " + age + " سال";
    }
    return null;
}

// ============================================================================
// FOCUSABLE PROFILE CARD
// ============================================================================

function FocusableProfileCard({
    profile,
    isSelected,
    onSelect,
    focusKey,
    isLoading,
}) {
    var { ref, focused } = useFocusable({
        focusKey: focusKey,
        onEnterPress: function () {
            if (!isLoading && !isSelected) {
                onSelect(profile._id);
            }
        },
    });

    var displayName = profile.fullName || profile.name || "پروفایل";
    var hasImage = profile.profileImage && profile.profileImage.length > 0;
    var profileImageUrl = resolveAuthMediaUrl(profile.profileImage);
    var initial = displayName.charAt(0) || "U";
    var ageLabel = getAgeRangeLabel(profile.ageRange);

    return (
        <ProfileCardWrapper>
            <ProfileCardStyled
                ref={ref}
                onClick={function () {
                    if (!isLoading && !isSelected) {
                        onSelect(profile._id);
                    }
                }}
                $focused={focused}
                $isSelected={isSelected}
            >
                <ProfileAvatar
                    $hasImage={hasImage}
                    $imageUrl={profileImageUrl}
                    $isSelected={isSelected}
                >
                    {!hasImage && <AvatarInner>{initial}</AvatarInner>}
                </ProfileAvatar>
                <ProfileName>{displayName}</ProfileName>
                {ageLabel && <AgeLabel>{ageLabel}</AgeLabel>}
                {isSelected && <ActiveBadge>فعال</ActiveBadge>}
            </ProfileCardStyled>
        </ProfileCardWrapper>
    );
}

// ============================================================================
// FOCUSABLE CANCEL BUTTON
// ============================================================================

function FocusableCancelButton({ onPress }) {
    var { ref, focused } = useFocusable({
        focusKey: "profile-switcher-cancel",
        onEnterPress: onPress,
    });

    return (
        <CancelButton ref={ref} onClick={onPress} $focused={focused}>
            بازگشت
        </CancelButton>
    );
}

// ============================================================================
// PROFILE SWITCHER MODAL
// ============================================================================

function ProfileSwitcherModal({
    open,
    profiles,
    currentProfileId,
    onClose,
    onSwitchComplete,
}) {
    var [isLoading, setIsLoading] = React.useState(false);
    // Get actual current profile ID from JWT token (source of truth)
    // Fallback to prop value if token decode fails
    var actualCurrentProfileId = getProfileIdFromToken() || currentProfileId;
    var currentIndex = profiles
        ? profiles.findIndex(function (p) {
            return p._id === actualCurrentProfileId;
        })
        : -1;
    if (currentIndex === -1) currentIndex = 0;

    var initialFocusKey =
        profiles && profiles.length > 0
            ? "profile-card-" + currentIndex
            : "profile-switcher-cancel";
    var { ref, focusKey } = useFocusable({
        focusKey: "PROFILE_SWITCHER_MODAL",
        focusable: false,
        trackChildren: true,
        autoRestoreFocus: true,
        preferredChildFocusKey: initialFocusKey,
        isFocusBoundary: true,
        focusBoundaryDirections: ["up", "down", "left", "right"],
    });

    // Set initial focus when modal opens
    useEffect(
        function () {
            if (!open) return undefined;

            var timers = [0, 80, 180, 360, 720].map(function (delay) {
                return setTimeout(function () {
                    setFocus(initialFocusKey);
                }, delay);
            });

            return function () {
                timers.forEach(function (timer) {
                    clearTimeout(timer);
                });
            };
        },
        [open, initialFocusKey]
    );

    // Handle ESC key to close
    useEffect(
        function () {
            if (!open) return;

            function handleKeyDown(e) {
                if (e.key === "Escape" || e.key === "Back" || e.keyCode === 10009) {
                    onClose();
                }
            }

            document.addEventListener("keydown", handleKeyDown);
            return function () {
                document.removeEventListener("keydown", handleKeyDown);
            };
        },
        [open, onClose]
    );

    // Handle profile switch
    var handleSelectProfile = useCallback(
        async function (profileId) {
            if (isLoading) return;

            try {
                setIsLoading(true);

                var data = await switchProfile(profileId);

                if (!data || !data.accessToken) {
                    setIsLoading(false);
                    toast.error("خطا در دریافت توکن");
                    return;
                }

                trackGoogleAnalyticsEvent("profile_selected", {
                    profile_id: profileId,
                    login_method: "profile_switch",
                });

                toast.success("پروفایل با موفقیت تغییر کرد");
                if (onSwitchComplete) {
                    onSwitchComplete(data);
                }
                onClose();

                // All old profile tokens are invalid after this point.
                performPwaRestart();
            } catch (error) {
                var errorMsg = getAuthErrorMessage(
                    error,
                    "خطا در تغییر پروفایل"
                );
                toast.error(errorMsg);
                setIsLoading(false);
            }
        },
        [isLoading, onClose, onSwitchComplete]
    );

    if (!open) return null;

    return (
        <FocusContext.Provider value={focusKey}>
            <Overlay $open={open} ref={ref}>
                <OverlayCell>
                    <ModalCard>
                        <ModalTitle>تغییر پروفایل</ModalTitle>

                        {isLoading ? (
                            <div style={{ padding: "40px 0" }}>
                                <LoadingSpinner />
                            </div>
                        ) : (
                            <>
                                <ProfilesContainer>
                                    {profiles &&
                                        profiles.map(function (profile, index) {
                                            var isSelected = profile._id === actualCurrentProfileId;
                                            return (
                                                <FocusableProfileCard
                                                    key={profile._id}
                                                    focusKey={"profile-card-" + index}
                                                    profile={profile}
                                                    isSelected={isSelected}
                                                    onSelect={handleSelectProfile}
                                                    isLoading={isLoading}
                                                />
                                            );
                                        })}
                                </ProfilesContainer>

                                <FocusableCancelButton onPress={onClose} />
                            </>
                        )}
                    </ModalCard>
                </OverlayCell>
            </Overlay>
        </FocusContext.Provider>
    );
}

export default ProfileSwitcherModal;
